import React, { useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import api from "@/lib/api";
import {
  Building2,
  Upload,
  Plus,
  Trash2,
  FileSpreadsheet,
  Truck,
  Train,
  Plane,
  Ship,
  Loader2,
  CheckCircle,
  Download,
  Package,
  Percent,
  Receipt,
  ArrowLeft,
  Info
} from "lucide-react";

const TRANSPORT_MODES = [
  { id: "road", label: "Road Transport", icon: Truck, rate: "₹8/kg" },
  { id: "rail", label: "Rail Freight", icon: Train, rate: "₹5/kg" },
  { id: "air", label: "Air Cargo", icon: Plane, rate: "₹25/kg" },
  { id: "water", label: "Sea Freight", icon: Ship, rate: "₹3/kg" },
];

const CARGO_TYPES = ["general", "fragile", "perishable", "hazardous", "electronics", "documents"];

const DISCOUNT_TIERS = [
  { min: 50, discount: 15, label: "50+ items: 15% off" },
  { min: 20, discount: 10, label: "20+ items: 10% off" },
  { min: 10, discount: 7, label: "10+ items: 7% off" },
  { min: 5, discount: 5, label: "5+ items: 5% off" },
];

const EMPTY_ITEM = {
  origin: "",
  destination: "",
  cargo_type: "general",
  weight_kg: "",
  dimensions: "",
  pickup_date: "",
  special_instructions: ""
};

export default function BulkBooking() {
  const { user } = useAuth();
  const navigate = useNavigate();
  
  const [companyDetails, setCompanyDetails] = useState({
    company_name: "",
    company_gst: "",
    contact_person: user?.name || "",
    contact_phone: "",
    contact_email: user?.email || ""
  });
  
  const [transportMode, setTransportMode] = useState("road");
  const [paymentTerms, setPaymentTerms] = useState("prepaid");
  const [items, setItems] = useState([{ ...EMPTY_ITEM }]);
  
  const [loading, setLoading] = useState(false);
  const [previewData, setPreviewData] = useState(null);
  const [showPreview, setShowPreview] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [createdBooking, setCreatedBooking] = useState(null);

  const addItem = () => {
    setItems([...items, { ...EMPTY_ITEM }]);
  };

  const removeItem = (index) => {
    if (items.length > 1) {
      setItems(items.filter((_, i) => i !== index));
    }
  };

  const updateItem = (index, field, value) => {
    const updated = [...items];
    updated[index] = { ...updated[index], [field]: value };
    setItems(updated);
  };

  const handleCSVUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    const formData = new FormData();
    formData.append("file", file);
    
    setLoading(true);
    try {
      const response = await api.post("/bulk-bookings/parse-csv", formData, {
        headers: { "Content-Type": "multipart/form-data" }
      });
      
      if (response.data.items?.length > 0) {
        setItems(response.data.items);
        toast.success(`Loaded ${response.data.valid_items} items from CSV`);
        
        if (response.data.errors?.length > 0) {
          toast.warning(`${response.data.errors.length} rows had errors`);
        }
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to parse CSV");
    } finally {
      setLoading(false);
      e.target.value = "";
    }
  };

  const validateForm = () => {
    if (!companyDetails.company_name || !companyDetails.contact_person || 
        !companyDetails.contact_phone || !companyDetails.contact_email) {
      toast.error("Please fill all company details");
      return false;
    }
    
    for (let i = 0; i < items.length; i++) {
      const item = items[i];
      if (!item.origin || !item.destination || !item.weight_kg || !item.pickup_date) {
        toast.error(`Item ${i + 1}: Please fill all required fields`);
        return false;
      }
      if (parseFloat(item.weight_kg) <= 0) {
        toast.error(`Item ${i + 1}: Weight must be greater than 0`);
        return false;
      }
    }
    
    return true;
  };

  const handlePreview = async () => {
    if (!validateForm()) return;
    
    setLoading(true);
    try {
      const payload = {
        ...companyDetails,
        transport_mode: transportMode,
        payment_terms: paymentTerms,
        items: items.map(item => ({
          ...item,
          weight_kg: parseFloat(item.weight_kg)
        }))
      };
      
      const response = await api.post("/bulk-bookings/preview", payload);
      setPreviewData(response.data);
      setShowPreview(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to generate preview");
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmBooking = async () => {
    setLoading(true);
    try {
      const payload = {
        ...companyDetails,
        transport_mode: transportMode,
        payment_terms: paymentTerms,
        items: items.map(item => ({
          ...item,
          weight_kg: parseFloat(item.weight_kg)
        }))
      };
      
      const response = await api.post("/bulk-bookings/create", payload);
      setCreatedBooking(response.data.booking);
      setShowPreview(false);
      setShowSuccess(true);
      toast.success("Bulk booking created successfully!");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create booking");
    } finally {
      setLoading(false);
    }
  };

  const downloadSampleCSV = () => {
    const csv = `origin,destination,cargo_type,weight_kg,dimensions,pickup_date,special_instructions
Mumbai,Delhi,general,50,100x50x50,2025-04-15,Handle with care
Chennai,Bangalore,electronics,25,80x40x40,2025-04-16,Fragile items
Kolkata,Hyderabad,documents,5,,2025-04-17,`;
    
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "bulk_booking_template.csv";
    a.click();
  };

  const getCurrentDiscount = useCallback(() => {
    const count = items.length;
    for (const tier of DISCOUNT_TIERS) {
      if (count >= tier.min) return tier;
    }
    return null;
  }, [items.length]);

  const currentDiscount = getCurrentDiscount();

  if (!user) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold mb-4">Sign in Required</h2>
          <p className="text-muted-foreground mb-4">Please sign in to create bulk bookings</p>
          <Button onClick={() => navigate("/auth")}>Sign In</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <div className="border-b border-border bg-card">
        <div className="max-w-6xl mx-auto px-4 py-6">
          <Button variant="ghost" size="sm" onClick={() => navigate(-1)} className="mb-4">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </Button>
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 bg-primary flex items-center justify-center">
              <Building2 className="w-7 h-7 text-primary-foreground" />
            </div>
            <div>
              <h1 className="text-3xl font-black tracking-tight">Bulk Booking</h1>
              <p className="text-muted-foreground">Book multiple shipments at once with volume discounts</p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 py-8">
        <div className="grid lg:grid-cols-3 gap-8">
          {/* Main Form */}
          <div className="lg:col-span-2 space-y-8">
            {/* Company Details */}
            <div className="border border-border p-6">
              <h2 className="text-lg font-bold mb-4 flex items-center gap-2">
                <Building2 className="w-5 h-5" />
                Company Details
              </h2>
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <Label>Company Name *</Label>
                  <Input
                    value={companyDetails.company_name}
                    onChange={(e) => setCompanyDetails({...companyDetails, company_name: e.target.value})}
                    placeholder="Acme Logistics Pvt. Ltd."
                    className="rounded-none mt-1"
                    data-testid="company-name-input"
                  />
                </div>
                <div>
                  <Label>GST Number</Label>
                  <Input
                    value={companyDetails.company_gst}
                    onChange={(e) => setCompanyDetails({...companyDetails, company_gst: e.target.value})}
                    placeholder="29ABCDE1234F1Z5"
                    className="rounded-none mt-1"
                  />
                </div>
                <div>
                  <Label>Contact Person *</Label>
                  <Input
                    value={companyDetails.contact_person}
                    onChange={(e) => setCompanyDetails({...companyDetails, contact_person: e.target.value})}
                    placeholder="John Doe"
                    className="rounded-none mt-1"
                  />
                </div>
                <div>
                  <Label>Contact Phone *</Label>
                  <Input
                    value={companyDetails.contact_phone}
                    onChange={(e) => setCompanyDetails({...companyDetails, contact_phone: e.target.value})}
                    placeholder="+91 98765 43210"
                    className="rounded-none mt-1"
                  />
                </div>
                <div className="md:col-span-2">
                  <Label>Contact Email *</Label>
                  <Input
                    type="email"
                    value={companyDetails.contact_email}
                    onChange={(e) => setCompanyDetails({...companyDetails, contact_email: e.target.value})}
                    placeholder="logistics@company.com"
                    className="rounded-none mt-1"
                  />
                </div>
              </div>
            </div>

            {/* Transport Mode */}
            <div className="border border-border p-6">
              <h2 className="text-lg font-bold mb-4">Transport Mode</h2>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {TRANSPORT_MODES.map((mode) => (
                  <button
                    key={mode.id}
                    type="button"
                    onClick={() => setTransportMode(mode.id)}
                    className={`p-4 border transition-all text-center ${
                      transportMode === mode.id 
                        ? "border-primary bg-primary/5" 
                        : "border-border hover:border-primary/50"
                    }`}
                    data-testid={`transport-mode-${mode.id}`}
                  >
                    <mode.icon className={`w-6 h-6 mx-auto mb-2 ${transportMode === mode.id ? "text-primary" : "text-muted-foreground"}`} />
                    <p className="text-sm font-medium">{mode.label}</p>
                    <p className="text-xs text-muted-foreground">{mode.rate}</p>
                  </button>
                ))}
              </div>
            </div>

            {/* Shipment Items */}
            <div className="border border-border p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-bold flex items-center gap-2">
                  <Package className="w-5 h-5" />
                  Shipment Items ({items.length})
                </h2>
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" onClick={downloadSampleCSV} className="rounded-none">
                    <Download className="w-4 h-4 mr-1" />
                    Template
                  </Button>
                  <label className="cursor-pointer">
                    <input type="file" accept=".csv" onChange={handleCSVUpload} className="hidden" />
                    <Button variant="outline" size="sm" className="rounded-none" asChild>
                      <span>
                        <Upload className="w-4 h-4 mr-1" />
                        Upload CSV
                      </span>
                    </Button>
                  </label>
                </div>
              </div>

              <div className="space-y-4">
                {items.map((item, index) => (
                  <div key={index} className="p-4 bg-secondary/30 border border-border">
                    <div className="flex items-center justify-between mb-3">
                      <span className="text-sm font-medium">Item {index + 1}</span>
                      {items.length > 1 && (
                        <Button variant="ghost" size="sm" onClick={() => removeItem(index)}>
                          <Trash2 className="w-4 h-4 text-destructive" />
                        </Button>
                      )}
                    </div>
                    <div className="grid md:grid-cols-3 gap-3">
                      <div>
                        <Label className="text-xs">Origin *</Label>
                        <Input
                          value={item.origin}
                          onChange={(e) => updateItem(index, "origin", e.target.value)}
                          placeholder="Mumbai"
                          className="rounded-none mt-1 h-9"
                        />
                      </div>
                      <div>
                        <Label className="text-xs">Destination *</Label>
                        <Input
                          value={item.destination}
                          onChange={(e) => updateItem(index, "destination", e.target.value)}
                          placeholder="Delhi"
                          className="rounded-none mt-1 h-9"
                        />
                      </div>
                      <div>
                        <Label className="text-xs">Cargo Type</Label>
                        <Select value={item.cargo_type} onValueChange={(v) => updateItem(index, "cargo_type", v)}>
                          <SelectTrigger className="rounded-none mt-1 h-9">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {CARGO_TYPES.map(type => (
                              <SelectItem key={type} value={type}>{type.charAt(0).toUpperCase() + type.slice(1)}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label className="text-xs">Weight (kg) *</Label>
                        <Input
                          type="number"
                          value={item.weight_kg}
                          onChange={(e) => updateItem(index, "weight_kg", e.target.value)}
                          placeholder="50"
                          className="rounded-none mt-1 h-9"
                        />
                      </div>
                      <div>
                        <Label className="text-xs">Pickup Date *</Label>
                        <Input
                          type="date"
                          value={item.pickup_date}
                          onChange={(e) => updateItem(index, "pickup_date", e.target.value)}
                          className="rounded-none mt-1 h-9"
                        />
                      </div>
                      <div>
                        <Label className="text-xs">Dimensions (LxWxH cm)</Label>
                        <Input
                          value={item.dimensions}
                          onChange={(e) => updateItem(index, "dimensions", e.target.value)}
                          placeholder="100x50x50"
                          className="rounded-none mt-1 h-9"
                        />
                      </div>
                    </div>
                    <div className="mt-3">
                      <Label className="text-xs">Special Instructions</Label>
                      <Textarea
                        value={item.special_instructions}
                        onChange={(e) => updateItem(index, "special_instructions", e.target.value)}
                        placeholder="Handle with care, fragile items..."
                        className="rounded-none mt-1 h-16 resize-none"
                      />
                    </div>
                  </div>
                ))}
              </div>

              <Button variant="outline" onClick={addItem} className="w-full mt-4 rounded-none">
                <Plus className="w-4 h-4 mr-2" />
                Add Another Item
              </Button>
            </div>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Discount Tiers */}
            <div className="border border-border p-6">
              <h3 className="font-bold mb-4 flex items-center gap-2">
                <Percent className="w-5 h-5 text-primary" />
                Volume Discounts
              </h3>
              <div className="space-y-2">
                {DISCOUNT_TIERS.map((tier, i) => (
                  <div 
                    key={i}
                    className={`flex items-center justify-between p-3 ${
                      currentDiscount?.min === tier.min 
                        ? "bg-primary/10 border border-primary" 
                        : "bg-secondary/50"
                    }`}
                  >
                    <span className="text-sm">{tier.label}</span>
                    {currentDiscount?.min === tier.min && (
                      <CheckCircle className="w-4 h-4 text-primary" />
                    )}
                  </div>
                ))}
              </div>
              {currentDiscount && (
                <p className="text-sm text-primary font-medium mt-4">
                  You qualify for {currentDiscount.discount}% discount!
                </p>
              )}
            </div>

            {/* Payment Terms */}
            <div className="border border-border p-6">
              <h3 className="font-bold mb-4">Payment Terms</h3>
              <Select value={paymentTerms} onValueChange={setPaymentTerms}>
                <SelectTrigger className="rounded-none">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="prepaid">Prepaid (Pay Now)</SelectItem>
                  <SelectItem value="credit_30">Net 30 Days</SelectItem>
                  <SelectItem value="credit_60">Net 60 Days</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground mt-2">
                <Info className="w-3 h-3 inline mr-1" />
                Credit terms available for verified businesses
              </p>
            </div>

            {/* Summary */}
            <div className="border border-border p-6 bg-card">
              <h3 className="font-bold mb-4 flex items-center gap-2">
                <Receipt className="w-5 h-5" />
                Order Summary
              </h3>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Total Items</span>
                  <span className="font-medium">{items.length}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Transport Mode</span>
                  <span className="font-medium capitalize">{transportMode}</span>
                </div>
                {currentDiscount && (
                  <div className="flex justify-between text-primary">
                    <span>Discount</span>
                    <span className="font-medium">{currentDiscount.discount}% off</span>
                  </div>
                )}
              </div>
              
              <Button 
                onClick={handlePreview} 
                disabled={loading}
                className="w-full mt-6 rounded-none"
                data-testid="preview-booking-btn"
              >
                {loading ? (
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                ) : (
                  <FileSpreadsheet className="w-4 h-4 mr-2" />
                )}
                Preview & Get Quote
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Preview Modal */}
      <Dialog open={showPreview} onOpenChange={setShowPreview}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold">Booking Preview</DialogTitle>
          </DialogHeader>
          
          {previewData && (
            <div className="space-y-6">
              <div className="p-4 bg-secondary/50 rounded-lg">
                <p className="font-medium">{previewData.company_name}</p>
                <p className="text-sm text-muted-foreground">
                  {previewData.total_items} items via {previewData.transport_mode} transport
                </p>
              </div>

              <div className="max-h-48 overflow-y-auto border border-border">
                <table className="w-full text-sm">
                  <thead className="bg-secondary sticky top-0">
                    <tr>
                      <th className="text-left p-2">Route</th>
                      <th className="text-left p-2">Type</th>
                      <th className="text-right p-2">Weight</th>
                      <th className="text-right p-2">Price</th>
                    </tr>
                  </thead>
                  <tbody>
                    {previewData.items?.map((item, i) => (
                      <tr key={i} className="border-t border-border">
                        <td className="p-2">{item.origin} → {item.destination}</td>
                        <td className="p-2 capitalize">{item.cargo_type}</td>
                        <td className="p-2 text-right">{item.weight_kg} kg</td>
                        <td className="p-2 text-right">₹{item.estimated_price}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="border border-border p-4 space-y-2">
                <div className="flex justify-between">
                  <span>Subtotal</span>
                  <span>₹{previewData.pricing?.subtotal?.toLocaleString()}</span>
                </div>
                <div className="flex justify-between text-primary">
                  <span>{previewData.pricing?.discount_tier} ({previewData.pricing?.discount_percent}%)</span>
                  <span>-₹{previewData.pricing?.discount_amount?.toLocaleString()}</span>
                </div>
                <div className="flex justify-between text-sm text-muted-foreground">
                  <span>Platform Fee (2%)</span>
                  <span>₹{previewData.pricing?.platform_fee?.toLocaleString()}</span>
                </div>
                <div className="flex justify-between text-sm text-muted-foreground">
                  <span>GST (18%)</span>
                  <span>₹{previewData.pricing?.gst?.toLocaleString()}</span>
                </div>
                <div className="h-px bg-border my-2" />
                <div className="flex justify-between text-lg font-bold">
                  <span>Total</span>
                  <span>₹{previewData.pricing?.total?.toLocaleString()}</span>
                </div>
              </div>

              <div className="flex gap-3">
                <Button variant="outline" onClick={() => setShowPreview(false)} className="flex-1 rounded-none">
                  Edit Booking
                </Button>
                <Button onClick={handleConfirmBooking} disabled={loading} className="flex-1 rounded-none" data-testid="confirm-booking-btn">
                  {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <CheckCircle className="w-4 h-4 mr-2" />}
                  Confirm Booking
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Success Modal */}
      <Dialog open={showSuccess} onOpenChange={setShowSuccess}>
        <DialogContent className="text-center">
          <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
            <CheckCircle className="w-8 h-8 text-primary" />
          </div>
          <DialogTitle className="text-xl font-bold">Booking Confirmed!</DialogTitle>
          <p className="text-muted-foreground">
            Your bulk booking has been created successfully.
          </p>
          {createdBooking && (
            <div className="mt-4 p-4 bg-secondary/50 rounded-lg text-left">
              <p className="text-sm"><strong>Booking ID:</strong> {createdBooking.id}</p>
              <p className="text-sm"><strong>Total Items:</strong> {createdBooking.total_items}</p>
              <p className="text-sm"><strong>Total Amount:</strong> ₹{createdBooking.pricing?.total?.toLocaleString()}</p>
              <p className="text-sm"><strong>Status:</strong> {createdBooking.status?.replace(/_/g, " ")}</p>
            </div>
          )}
          <div className="flex gap-3 mt-6">
            <Button variant="outline" onClick={() => navigate("/dashboard/bookings")} className="flex-1 rounded-none">
              View Bookings
            </Button>
            <Button onClick={() => { setShowSuccess(false); setItems([{ ...EMPTY_ITEM }]); }} className="flex-1 rounded-none">
              New Booking
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
